import { inject, Injectable } from '@angular/core';
import { getStepEvent, StudentDetailsDataLayerMapperService } from '@lx/quote-tool/shared/google-analytics';
import { GoogleAnalyticsDataLayerService } from '@lx/shared/google-analytics';
import { combineLatest, map, Observable, of, tap } from 'rxjs';
import { GoogleAnalyticsParametersType } from './google-analytics-parameters.type';
import { QuoteToolLiteStore } from '../store';
import { QuoteDataLayerStrategy } from './quote-data-layer.strategy';
import { switchMap } from 'rxjs/operators';
import { LoggerService } from '@lx/shared/logger';
import { QUOTE_DATA_LAYER_STRATEGIES } from './quote-data-layer.strategy.token';
import { ActivatedRoute } from '@angular/router';
import { Flow } from '@lx/quote-tool/lite/shared/types';

@Injectable({
  providedIn: 'root',
})
export class QuoteToolLiteGoogleAnalyticsDataLayerService implements GoogleAnalyticsDataLayerService<GoogleAnalyticsParametersType> {
  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly studentDetailsDataLayerMapperService = inject(StudentDetailsDataLayerMapperService);
  private readonly dataLayerStrategies = inject(QUOTE_DATA_LAYER_STRATEGIES);
  private readonly route = inject(ActivatedRoute);

  private readonly loggerService = inject(LoggerService);

  private quoteDataLayerStrategies = new Map<Flow, QuoteDataLayerStrategy>();

  constructor() {
    for (const strategy of this.dataLayerStrategies) {
      this.quoteDataLayerStrategies.set(strategy.flow, strategy);
    }
  }

  fillDataLayer(): Observable<Partial<GoogleAnalyticsParametersType>> {
    let strategy: QuoteDataLayerStrategy | undefined;

    return this.quoteToolLiteStore.activeFlow$.pipe(
      tap((activeFlow) => (strategy = this.quoteDataLayerStrategies.get(activeFlow))),
      switchMap((activeFlow) => {
        // If there is an active flow but no strategy, log an error but continue
        if (activeFlow && !strategy) {
          this.loggerService.error(`No strategy found for the current flow ${activeFlow}`);
        }

        return combineLatest([strategy?.buildDataLayer() || of({}), this.quoteToolLiteStore.studentDetails$, this.quoteToolLiteStore.deeplinkType$]);
      }),
      map(([activeFlowQuoteDataLayer, studentDetails, deeplinkType]) => {
        return {
          step: getStepEvent(this.route),
          deeplink_type: deeplinkType,
          ...activeFlowQuoteDataLayer,
          ...this.studentDetailsDataLayerMapperService.mapStudentDetailsToDataLayer(studentDetails),
        };
      }),
    );
  }

  deepMerge(
    target: Partial<GoogleAnalyticsParametersType> | null | undefined,
    source: Partial<GoogleAnalyticsParametersType>,
  ): Partial<GoogleAnalyticsParametersType> {
    const safeTarget = target ?? {};
    const previousQuotes = safeTarget.quotes || [];
    const newQuotes = source?.quotes || [];

    const previousNonDuplicateQuotes = previousQuotes.filter((prevQuote) => !newQuotes.some((newQuote) => prevQuote.quote === newQuote.quote));

    const mergedQuotes = [...previousNonDuplicateQuotes, ...newQuotes];

    // Keep only the last 3 quotes
    const lastThreeQuotes = mergedQuotes.slice(-3);

    return {
      ...safeTarget, // keep other fields from target
      ...source, // override other fields with source
      quotes: lastThreeQuotes,
    };
  }
}
